import React from "react";
import { createRoot } from "react-dom/client";
import { Provider } from "react-redux";
import { setupStore } from "slices/store";

import "style.css";
import "scss/app.scss";
import ErrorFallbackModal from "components/Modal/ErrorFallbackModal";
import SpinnerWithLoadMask from "components/SpinnerWithLoadMask";
import { ErrorBoundary } from "react-error-boundary";
import UserAdmin from "pages/userAdmin";
import SystemAdmin from "pages/systemAdmin";
import Discovery from "pages/discovery";
import Dictionary from "pages/dictionary";
import LicenseSetup from "pages/licenseSetup";
import {
  Alert,
  alertTypes,
} from "@visiblethread/react-components/dist/components/Alert";
import DictionarySearch from "pages/dictionarySearch";
import Folders from "pages/folders";
import HomeSummary from "pages/homeSummary";
import FolderEmptySummary from "pages/folderEmptySummary";
import FolderSummary from "pages/folderSummary";
import DocumentSummary from "pages/documentSummary";
import { htmlDecode } from "utils";
import DictionarySearchFolder from "layout/dictionarySearch/folder";
import SettingsPage from "pages/settings";
import HelpPage from "pages/help";

// If you want to test any page / layout / component you can render it here

if (process.env.NODE_ENV === "development") {
  global.UserInfo = {
    authType: "UsernamePassword",
    roles: "user,settings,dictionary,user_admin,system_admin",
    workspaceId: "7",
    workspaces: [
      {
        workspaceName: "Default Sandbox",
        active: false,
        dateModified: 0,
        lastSignInTime: 0,
        workspaceId: 7,
        numUsers: 0,
        numActiveUsers: 0,
        anonymizeUsers: false,
        reservedLicenses: 0,
      },
    ],
    workspaceName: "Default Sandbox",
    userName: "fintan",
    licensed: false,
    location: "System Admin",
  };
  global.Application = {
    buildNumber: "30080",
    productVersion: "5.1.7",
    hasLicenseExpired: false,
  };

  const container = document.querySelector("body");
  const root = createRoot(container);

  root.render(
    <Provider store={setupStore()}>
      <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
        <SpinnerWithLoadMask opacity={0.7} fullScreen />
        <DictionarySearchFolder />
      </ErrorBoundary>
    </Provider>
  );
} else {
  window.renderVTDiscovery = (element, vtId, folderId, title) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <Discovery vtId={vtId} folderId={folderId} title={title} />
      </Provider>
    );
  };

  window.renderSystemAdmin = () => {
    const container = document.querySelector("#systemAdmin");
    const root = createRoot(container);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <SystemAdmin />
          Documents
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderUserAdmin = () => {
    const container = document.querySelector("#userAdmin");
    const root = createRoot(container);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <UserAdmin />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderDictionaries = () => {
    const container = document.querySelector("#dictionaries");
    const root = createRoot(container);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <Dictionary />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderLicenseSetup = () => {
    const container = document.querySelector("#licenseSetup");
    const root = createRoot(container);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <LicenseSetup />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderDictionarySearch = (element, documentId, folderId, title) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <DictionarySearch
            documentId={documentId}
            folderId={folderId}
            title={htmlDecode(title)}
          />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderHomeSummary = (element) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <HomeSummary />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderFolderEmptySummary = (element) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <FolderEmptySummary />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderFolderSummary = (element) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <FolderSummary />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderDocumentSummary = (element) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <DocumentSummary />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderFolders = (element) => {
    const root = createRoot(element);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <Folders />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderGlobalAlert = ({
    variant,
    text,
    autoDismissible,
    hideCloseButton,
    onClose,
  }) => {
    const decodeMessage = (message) => {
      const txt = document.createElement("textarea");
      txt.innerHTML = message;
      return txt.value;
    };
    const alertContainer = document.createElement("div");
    alertContainer.setAttribute("id", "global_alert_container");
    alertContainer.style.position = "absolute";
    document.body.appendChild(alertContainer);

    const container = document.getElementById("global_alert_container");
    const root = createRoot(container);

    root.render(
      <Alert
        type={alertTypes.global}
        variant={variant}
        text={decodeMessage(text)}
        autoDismissible={autoDismissible}
        hideCloseButton={hideCloseButton}
        onClose={onClose}
      />
    );
  };

  window.removeGlobalAlert = () => {
    const alertContainer = document.getElementById("global_alert_container");
    if (alertContainer) {
      document.body.removeChild(
        document.getElementById("global_alert_container")
      );
    }
  };

  window.renderSettings = () => {
    const container = document.querySelector("#settingsPage");
    const root = createRoot(container);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <SettingsPage />
        </ErrorBoundary>
      </Provider>
    );
  };

  window.renderHelp = () => {
    const container = document.querySelector("#helpPage");
    const root = createRoot(container);

    root.render(
      <Provider store={setupStore()}>
        <ErrorBoundary FallbackComponent={ErrorFallbackModal}>
          <SpinnerWithLoadMask opacity={0.7} fullScreen />
          <HelpPage />
        </ErrorBoundary>
      </Provider>
    );
  };
}
